import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router';

import { LoadingButton } from '@mui/lab';
import {
  GridRowId,
  GridColDef,
  DataGridPro,
  GridRenderCellParams,
  GridRowSelectionModel,
} from '@mui/x-data-grid-pro';
import {
  Box,
  Dialog,
  Checkbox,
  TextField,
  IconButton,
  Typography,
  DialogTitle,
  Autocomplete,
  DialogActions,
  DialogContent,
} from '@mui/material';

import { useSelector } from '../../../redux/store';
import useLocales from '../../../hooks/useLocales';
import { PATH_STANDARD } from '../../../routes/paths';
import useNotifications from '../../../hooks/useNotifications';
import { setCheckedItems } from '../../../redux/slices/document';
import { DLXDriveFolderListItem } from '../../../@types/dlx-drive/types';
import { getFolderList, generateContainer } from '../../../services/dlxDriveService';
import { FOLDER_ACTIONS_MENU_ICON_SIZE } from '../../../utils/constants/dlxDriveConstants';
import { addStopLoadingState, addStartLoadingState } from '../../../redux/slices/dlxDrive';
import DocumentComposeArchive, {
  ADOC_TYPE,
  SIGNED_FILE_TYPE,
} from '../../documents/DocumentComposeArchive';

// ----------------------------------------------------------------

const MAIN_FILE_TYPE = ['pdf', 'doc', 'docx', 'xls', 'xlsx', 'ppt', 'pptx', 'jpg', 'jpeg', 'png'];

type DLXDriveArchiveDialogProps = {
  isArchiveDialogOpen: boolean;
  setIsArchiveDialogOpen: (value: boolean) => void;
};

const FolderArchiveDialog: React.FC<DLXDriveArchiveDialogProps> = ({
  isArchiveDialogOpen,
  setIsArchiveDialogOpen,
}) => {
  const { t } = useLocales();
  const params = useParams();
  const currentFolderId = params.folderId ?? params.id;
  const { isLoading } = useSelector((state) => state.dlxDrive);
  const { checkedItems } = useSelector((state) => state.document);
  const [signedFileType, setSignedFileType] = useState<number>(SIGNED_FILE_TYPE.ADOC);
  const [adocType, setAdocType] = useState<number>(ADOC_TYPE.GEDOC);
  const [name, setName] = useState<string>('');
  const [selectedFolder, setSelectedFolder] = useState<DLXDriveFolderListItem | null>(null);
  const [folderList, setFolderList] = useState<DLXDriveFolderListItem[]>([]);
  const { enqueueSuccess } = useNotifications();
  const navigate = useNavigate();

  // --- Single-checkbox selection model (v8): { type, ids:Set } ---
  const [rowSelectionModel, setRowSelectionModel] = useState<GridRowSelectionModel>({
    type: 'include',
    ids: new Set<GridRowId>(),
  });
  const selectedRowId: GridRowId | null = rowSelectionModel.ids.size
    ? (Array.from(rowSelectionModel.ids).pop() ?? null)
    : null;

  // Build rows from checkedItems
  const rows = checkedItems.map((item) => ({
    id: item.id,
    name: item.name,
    extension: item.extension,
  }));

  // Toggle handler for our custom checkbox column (keeps only one id)
  const handleCheckboxToggle = (id: GridRowId, nextChecked: boolean) => {
    setRowSelectionModel({
      type: 'include',
      ids: nextChecked ? new Set<GridRowId>([id]) : new Set<GridRowId>(),
    });
  };

  const columns: GridColDef<(typeof rows)[number]>[] = [
    {
      field: 'main',
      headerName: t('documents.main'),
      width: 100,
      sortable: false,
      filterable: false,
      disableColumnMenu: true,
      align: 'center',
      headerAlign: 'center',
      renderCell: (params: GridRenderCellParams) => {
        const { extension } = params.row;

        const checked = rowSelectionModel.ids.has(params.id);

        if (!MAIN_FILE_TYPE.includes(extension)) {
          return '';
        }

        return (
          <Checkbox
            checked={checked && MAIN_FILE_TYPE.includes(extension)}
            onChange={(e) => handleCheckboxToggle(params.id, e.target.checked)}
            slotProps={{ input: { 'aria-label': `Select row ${params.id}` } }}
          />
        );
      },
    },
    {
      field: 'id',
      headerName: t('dlxDrive.dataGrid.columnId'),
      width: 100,
    },
    {
      field: 'name',
      headerName: t('dlxDrive.dataGrid.columnName'),
      flex: 1,
    },
    {
      field: 'extension',
      headerName: t('dlxDrive.dataGrid.columnType'),
      width: 100,
    },
    {
      field: 'remove',
      headerName: t('dlxDrive.dataGrid.columnRemove'),
      headerAlign: 'center',
      align: 'center',
      sortable: false,
      width: 90,
      renderCell: (params) => {
        const onClick = () => {
          const { id } = params.row;
          const newCheckedItems = checkedItems.filter((item) => item.id !== id);
          setCheckedItems(newCheckedItems);
          // If the removed one was selected — clear selection
          if (rowSelectionModel.ids.has(id)) {
            setRowSelectionModel({ type: 'include', ids: new Set<GridRowId>() });
          }
        };
        return (
          <IconButton onClick={onClick} id="DjuXCP">
            <img
              src="/icons/dlx-drive/actions/delete.svg"
              width={FOLDER_ACTIONS_MENU_ICON_SIZE}
              height={FOLDER_ACTIONS_MENU_ICON_SIZE}
              alt={t('delete')}
            />
          </IconButton>
        );
      },
    },
  ];

  const handleClose = () => {
    setSelectedFolder(null);
    setIsArchiveDialogOpen(false);
    setRowSelectionModel({ type: 'include', ids: new Set<GridRowId>() });
  };

  const handleArchive = async () => {
    addStartLoadingState();

    try {
      const documentIds = [...checkedItems.filter((item) => !item.isFolder).map((item) => item.id)];

      if (documentIds.length > 0 && documentIds[0].id !== selectedRowId) {
        const mainFileIndex = documentIds.findIndex((id) => id === selectedRowId);

        documentIds.unshift(documentIds.splice(mainFileIndex, 1)[0]);
      }

      const model = {
        documentIds,
        name: `${name}.${SIGNED_FILE_TYPE[signedFileType].toLowerCase()}`,
        folderId: selectedFolder ? selectedFolder.id : undefined,
        signedFileType,
        adocType,
      };

      const id = await generateContainer(model);

      setCheckedItems([]);

      enqueueSuccess('dlxDrive.notifications.documentCreatedSuccessfully', { name: `'${name}'` });

      navigate(`${PATH_STANDARD.documents}/${id}`);
    } catch (error) {
      console.error(error);
    } finally {
      addStopLoadingState();
      setIsArchiveDialogOpen(false);
    }
  };

  const fetchFolderList = async () => {
    let url: string = '';

    checkedItems.forEach((item) => {
      if (item.isFolder) {
        url += `ids=${item.id}&`;
      }
    });

    const folderList = await getFolderList(true, url.length > 0 ? url : undefined);

    const filteredFolderList = folderList.filter((f) => !checkedItems.some((c) => c.id === f.id));

    setFolderList(filteredFolderList);

    if (currentFolderId) {
      const match = filteredFolderList.find((f) => f.id === Number(currentFolderId));
      if (match) {
        setSelectedFolder(match);
      }
    }

    addStopLoadingState();
  };

  useEffect(() => {
    if (checkedItems.length === 0) {
      setIsArchiveDialogOpen(false);
      setRowSelectionModel({ type: 'include', ids: new Set<GridRowId>() });
    }
  }, [checkedItems]);

  useEffect(() => {
    if (isArchiveDialogOpen) {
      fetchFolderList();

      if (checkedItems && checkedItems.length > 0) {
        setName(checkedItems[0].name.replace(/\.[^/.]+$/, ''));
      }
    }
  }, [isArchiveDialogOpen]);

  useEffect(() => {
    const mainFileName = checkedItems.filter((item) => item.id === selectedRowId);

    if (mainFileName && mainFileName.length > 0) {
      setName(mainFileName[0].name.replace(/\.[^/.]+$/, ''));
    }
  }, [selectedRowId]);

  return (
    <Dialog open={isArchiveDialogOpen} fullWidth maxWidth="md" onClose={handleClose}>
      <DialogTitle>
        {t('dlxDrive.dialogs.createContainer', { type: `${SIGNED_FILE_TYPE[signedFileType]}` })}
      </DialogTitle>

      <DialogContent>
        <DocumentComposeArchive
          name={name}
          setName={setName}
          signedFileType={signedFileType}
          setSignedFileType={setSignedFileType}
          adocType={adocType}
          setAdocType={setAdocType}
        />

        <Autocomplete
          id="move-folders"
          sx={{ my: 2 }}
          value={selectedFolder}
          options={folderList.sort((a, b) => -b.firstLetter.localeCompare(a.firstLetter))}
          groupBy={(option: DLXDriveFolderListItem) => option.firstLetter}
          getOptionLabel={(option) => `${option.name} (id: ${option.id})`}
          isOptionEqualToValue={(option, value) => option.id === value.id}
          onChange={(event: any, newValue: DLXDriveFolderListItem | null) => {
            if (newValue) {
              setSelectedFolder(newValue);
            }
          }}
          renderInput={(params) => (
            <TextField
              {...params}
              label={t('dlxDrive.dialogs.folderList')}
              placeholder="Favorites"
            />
          )}
        />

        {signedFileType === SIGNED_FILE_TYPE.ADOC && (
          <Box
            sx={{ display: 'flex', justifyContent: 'left', alignItems: 'center', gap: 1, mb: 1 }}
          >
            <Typography>{t('documents.mainDocument')}:</Typography>

            <Typography color={selectedRowId ? 'success' : 'error'} variant="subtitle1">
              {selectedRowId
                ? `${checkedItems.filter((item) => item.id === selectedRowId)[0].name} (ID: ${selectedRowId})`
                : `${t('documents.noMainDocumentSelected')} (${MAIN_FILE_TYPE.join(', ')})`}
            </Typography>
          </Box>
        )}

        <DataGridPro
          columns={columns}
          rows={rows}
          rowHeight={50}
          sx={{ width: '100%' }}
          disableColumnFilter
          disableColumnMenu
          disableMultipleColumnsSorting
          disableChildrenSorting
          localeText={{ footerTotalRows: t('dlxDrive.dataGrid.footerTotalRows') }}
          // We manage single selection ourselves:
          checkboxSelection={false}
          rowSelectionModel={rowSelectionModel}
          onRowSelectionModelChange={(model) => {
            // Grid may still emit on row click; keep 0–1 invariant.
            const ids = Array.from(model.ids);
            const nextSet =
              ids.length > 0 ? new Set<GridRowId>([ids[ids.length - 1]]) : new Set<GridRowId>();
            setRowSelectionModel({ type: 'include', ids: nextSet });
          }}
          disableRowSelectionOnClick // use only our checkbox to toggle
          hideFooterSelectedRowCount
          columnVisibilityModel={{ main: signedFileType === SIGNED_FILE_TYPE.ADOC }}
        />
      </DialogContent>

      <DialogActions sx={{ display: 'flex', justifyContent: 'left', textAlign: 'center', gap: 1 }}>
        <LoadingButton loading={isLoading} variant="outlined" onClick={handleClose} id="mz5nnxqa">
          {`${t('shared.buttons.cancel')}`}
        </LoadingButton>

        <LoadingButton
          disabled={signedFileType === SIGNED_FILE_TYPE.ADOC && !selectedRowId}
          loading={isLoading}
          variant="contained"
          onClick={handleArchive}
          id="yd0sown5"
        >
          {`${t('shared.buttons.createContainerDocument', { type: `${SIGNED_FILE_TYPE[signedFileType]}` })}`}
        </LoadingButton>
      </DialogActions>
    </Dialog>
  );
};

export default FolderArchiveDialog;
